import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  Grid,
  Typography,
  IconButton,
  withStyles,
  Theme,
  Tooltip,
  Hidden,
} from '@material-ui/core';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { SpecialProps } from 'components/SpecialListView/type';
import CodeVO from 'commons/vo/CodeVO';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/reducers/type';
import { useStyles } from 'components/AssetListView/styles';
import asset from 'redux/reducers/asset';
import { eventPropagationPrevent } from 'commons/utils/commonUtil';
import CategoryVO from 'commons/vo/CategoryVO';
import StorageVO from 'commons/vo/StorageVO';
import GetAppIcon from '@material-ui/icons/GetApp';
import SwitchVideoIcon from '@material-ui/icons/SwitchVideo';
import { useAsset, useLibrary } from 'hooks';
import { File, Resolution } from 'commons/constants/KeyFlowConstants';
import TimeCode from 'commons/utils/TimeCode';
import LibraryParamVO from 'commons/vo/param/LibraryParamVO';
import WkblVideoVO from 'commons/vo/WkblVideoVO';
import { getDayOfWeek } from 'commons/utils/DateUtils';
import DownloadAssetSplitButton from 'components/DownloadAssetSplitButton';

const HtmlTooltip = withStyles((theme: Theme) => ({
  tooltip: {
    backgroundColor: '#FFF',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(14),
    border: '1px solid #dadde9',
  },
}))(Tooltip);

function Special({ specialVideo, index }: SpecialProps) {
  const classes = useStyles();

  const { onInsertLibrary } = useLibrary();
  const [menuButton, setMenuButton] = React.useState<null | HTMLButtonElement>(null);
  const [inTimecodeUtil, setInTimecodeUtil] = useState<TimeCode>(() => {
    const timecodeUtil = new TimeCode(specialVideo.mediaFps);
    timecodeUtil.setFrames(specialVideo.inPoint);
    return timecodeUtil;
  });
  const [outTimecodeUtil, setOutTimecodeUtil] = useState<TimeCode>(() => {
    const timecodeUtil = new TimeCode(specialVideo.mediaFps);
    timecodeUtil.setFrames(specialVideo.outPoint);
    return timecodeUtil;
  });
  const [durationTimecodeUtil] = useState<TimeCode>(() => {
    const timecodeUtil = new TimeCode(specialVideo.mediaFps);
    timecodeUtil.setFrames(specialVideo.outPoint - specialVideo.inPoint);
    return timecodeUtil;
  });

  const { onRequestAssetDownload, onRequestAssetDownloadCloudfront } = useAsset();

  const {
    videoTypeList,
    wkblGameTypeList,
    kfScriptRefRangeList: rangeCodeList,
    wkblSeasonList,
    kfScriptTypeList,
  } = useSelector((state: RootState) => state.code);

  const { specialVideoList, pagination, searchCondition } = useSelector(
    (state: RootState) => state.wkblSpecialVideo,
  );
  const { storageList } = useSelector((state: RootState) => state.storage);
  const { categoryList } = useSelector((state: RootState) => state.category);

  const storage: StorageVO | null = useMemo(() => {
    let result = null;
    const category = categoryList.filter(({ id }: CategoryVO) => id === specialVideo.categoryId)[0];
    if (category) {
      result = storageList.filter(({ id }: StorageVO) => id === category.storageId)[0];
    }
    return result;
  }, [categoryList, storageList, specialVideo.categoryId]);

  const symbolicPath: string = useMemo(() => {
    if (storage) {
      return storage.symbolicPath;
    }
    return '';
  }, [storage]);

  const videoNo: number = useMemo(() => {
    const { pageNumber, returnCount } = pagination;
    const returnCnt = returnCount || searchCondition.retr;
    return returnCnt * pageNumber + index + 1;
  }, [pagination, index]);

  const seasonName = useMemo(() => {
    const seasonItem: CodeVO[] =
      wkblSeasonList.filter((item: CodeVO) => item.id === Number(specialVideo.season)) || [];
    if (seasonItem.length > 0) {
      return seasonItem[0].name || '없음';
    }
    return '없음';
  }, [wkblSeasonList, specialVideo.season]);

  const gameTypeName = useMemo(() => {
    const gameTypeItem: CodeVO[] =
      wkblGameTypeList.filter((item: CodeVO) => item.id === Number(specialVideo.gameType)) || [];
    if (gameTypeItem.length > 0) {
      return gameTypeItem[0].name || '없음';
    }
    return '없음';
  }, [wkblGameTypeList, specialVideo.gameType]);

  const rangeCodeName = useMemo(() => {
    const rangeCode: CodeVO[] =
      rangeCodeList.filter((item: CodeVO) => item.codevalue === String(specialVideo.subType)) || [];
    if (rangeCode.length > 0) {
      return rangeCode[0].name || '';
    }
    return '';
  }, [rangeCodeList, specialVideo]);

  const team1LogoPath = useMemo(() => {
    return specialVideo.getTeam1LogoPath(symbolicPath);
  }, [asset, symbolicPath]);

  const team2LogoPath = useMemo(() => {
    return specialVideo.getTeam2LogoPath(symbolicPath);
  }, [asset, symbolicPath]);

  const { team1, team2 } = useMemo(() => {
    return specialVideo.getTeamLogoPath();
  }, [specialVideo]);

  const onPopupAssetDetail = useCallback(
    (event: React.MouseEvent) => {
      const { refId, id, title, subType } = specialVideo;
      const inTime = inTimecodeUtil.getSeconds();
      const outTime = outTimecodeUtil.getSeconds();
      const encodedKeyword = encodeURIComponent(subType);
      let redirectUrl = `/preview/${id}`;
      if (refId !== id && rangeCodeName) {
        // redirectUrl = `/preview/${refId}?in=${inTime}&out=${outTime}&keyword=${encodedKeyword}`;
        redirectUrl = `/preview/${refId}?in=${inTime}&out=${outTime}&subType=${encodedKeyword}`;
      } else if (refId !== id && !rangeCodeName) {
        redirectUrl = `/preview/${refId}?in=${inTime}&out=${outTime}`;
      }
      window.open(redirectUrl);
    },
    [specialVideo, inTimecodeUtil, outTimecodeUtil],
  );

  const onEventPrevent = useCallback((event: React.MouseEvent) => {
    eventPropagationPrevent(event);
  }, []);

  const onDownloadLowres = useCallback(
    (event: React.MouseEvent) => {
      const { id } = specialVideo;
      onRequestAssetDownload({
        videoId: id,
        storageId: storage ? storage.id : 0,
        type: File.LOWRES,
      });
    },
    [specialVideo, storage, onRequestAssetDownload],
  );

  const onRequestLibrary = useCallback(
    (event: React.MouseEvent) => {
      const { categoryId, title, inPoint, outPoint, refId, lowresPath } = specialVideo;
      const param = new LibraryParamVO({
        contents: title,
        inPoint,
        outPoint,
        resolution: Resolution.LOWRES,
        categoryId,
        videoId: refId,
        assetTitle: title,
        startTimecode: inTimecodeUtil.getTimeCodeWithFrame(),
        endTimecode: outTimecodeUtil.getTimeCodeWithFrame(),
        duration: durationTimecodeUtil.getSeconds(),
        isClean: false,
      });
      onInsertLibrary(param);
    },
    [specialVideo, storage, onInsertLibrary],
  );

  const onDownloadLowresCloudFront = useCallback(() => {
    const { categoryId, lowresPath, title, formattedGameDate } = specialVideo;
    const gameDate = formattedGameDate.replace(/-/g, '');
    onRequestAssetDownloadCloudfront({
      categoryId,
      fileName: lowresPath,
      downloadFileName: `${gameDate}_${title}.mp4`,
    });
  }, []);

  const onOpenMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuButton(event.currentTarget);
  }, []);

  const onCloseMenu = useCallback(() => {
    setMenuButton(null);
  }, []);

  return (
    <div className={clsx(classes.rowContainer, classes.row)} onClick={onPopupAssetDetail}>
      <Hidden mdDown>
        <span className={clsx(classes.headerColumn, classes.seasonContainer)}>
          <div>{seasonName}</div>
        </span>
        <span className={clsx(classes.headerColumn, classes.typeContainer)}>
          <div>{gameTypeName}</div>
        </span>
        <span
          className={clsx(classes.headerColumn, classes.titleContainer, classes.alignLeftColumn)}
        >
          <div>{specialVideo.title}</div>
        </span>
        {/* 홈 팀 */}
        <HtmlTooltip
          title={
            <React.Fragment>
              <Typography>{specialVideo.team1Name}</Typography>
            </React.Fragment>
          }
        >
          <span className={clsx(classes.headerColumn, classes.teamContainer)}>
            {specialVideo.team1 && team1.db && specialVideo.team1Name && (
              <div>
                <img src={team1.db}></img>
                <span>{specialVideo.team1Name}</span>
              </div>
            )}
          </span>
        </HtmlTooltip>
        {/* 어웨이 팀 */}
        <HtmlTooltip
          title={
            <React.Fragment>
              <Typography>{specialVideo.team2Name}</Typography>
            </React.Fragment>
          }
        >
          <span className={clsx(classes.headerColumn, classes.teamContainer)}>
            {specialVideo.team2 && team2.db && specialVideo.team2Name && (
              <div>
                <img src={team2.db}></img>
                <span>{specialVideo.team2Name}</span>
              </div>
            )}
          </span>
        </HtmlTooltip>
        <span className={clsx(classes.headerColumn, classes.gameDateContainer)}>
          <div>
            {specialVideo.formattedGameDate} ({getDayOfWeek(specialVideo.formattedGameDate)})
          </div>
        </span>
        <span
          className={clsx(classes.headerColumn, classes.menuContainer)}
          onClick={onEventPrevent}
        >
          {specialVideo.isVideo ? (
            <Tooltip title="다운로드">
              <DownloadAssetSplitButton
                asset={specialVideo}
                onDownloadClick={onDownloadLowresCloudFront}
                isListView={true}
              />
            </Tooltip>
          ) : (
            <Tooltip title="부분인출">
              <IconButton onClick={onRequestLibrary}>
                <SwitchVideoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {/* {specialVideo.isVideo ? (
            <Tooltip title="다운로드">
              <IconButton onClick={onDownloadLowres}>
                <GetAppIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="부분인출">
              <IconButton onClick={onRequestLibrary}>
                <SwitchVideoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )} */}
        </span>
      </Hidden>
      <Hidden lgUp>
        <div className={clsx(classes.headerColumn, classes.verticalColumn)}>
          <span>{seasonName}</span>
          <span>
            <div>{gameTypeName}</div>
          </span>
          <span>
            <div className={classes.textEllipsis}>{specialVideo.title}</div>
          </span>
          <span className={clsx(classes.gameDateContainer)}>
            <div>{specialVideo.formattedGameDate}</div>
          </span>
        </div>
        <span
          className={clsx(classes.headerColumn, classes.menuContainer)}
          onClick={onEventPrevent}
        >
          {specialVideo.isVideo ? (
            <Tooltip title="다운로드">
              <DownloadAssetSplitButton
                asset={specialVideo}
                onDownloadClick={onDownloadLowresCloudFront}
                isListView={true}
              />
            </Tooltip>
          ) : (
            <Tooltip title="부분인출">
              <IconButton onClick={onRequestLibrary}>
                <SwitchVideoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {/* {specialVideo.isVideo ? (
            <Tooltip title="다운로드">
              <IconButton onClick={onDownloadLowres}>
                <GetAppIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="부분인출">
              <IconButton onClick={onRequestLibrary}>
                <SwitchVideoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )} */}
        </span>
      </Hidden>
    </div>
  );
}

export default Special;
